import React, { useState, useEffect, useRef } from 'react';

/**
 * COMPONENTE: KARAOKE SCREEN (OVERLAY MODE)
 * ------------------------------------------------------------------
 * Diseñado para funcionar como una capa transparente sobre video (OBS).
 * Muestra una barra superior con la letra sincronizada.
 * ------------------------------------------------------------------
 */
const Karaoke = ({ data }) => {

    // --- ESTADOS ---
    const [currentLine, setCurrentLine] = useState(null);       // Objeto con la línea actual { text, mode, duration }
    const [isVisible, setIsVisible] = useState(true);           // Control maestro de visibilidad
    const [isFillingEnabled, setIsFillingEnabled] = useState(true); // Activar/Desactivar animación
    
    // Configuración de colores (Valores por defecto)
    // const [colors, setColors] = useState({ base: '#ffffff', fill: '#ffd400', stroke: '#000000' });
    const [colors, setColors] = useState({ base: '#ffffff', fill: '#dc2626', stroke: '#000000' });

    // --- REFERENCIAS (Para manipulación directa del DOM en animaciones) ---
    const transitionRef = useRef(null); // Referencia al div que se rellena (Capa Fill)

    // -------------------------------------------------------------------------
    // 1. GESTIÓN DEL BODY (Fondo Transparente)
    // -------------------------------------------------------------------------
    useEffect(() => {
        // Agregamos la clase al body para asegurar fondo transparente en OBS
        document.body.classList.add('Karaoke__Screen__Body');
        
        return () => {
            document.body.classList.remove('Karaoke__Screen__Body');
        }
    }, []);

    // -------------------------------------------------------------------------
    // 2. ESCUCHA DE DATOS (WEBSOCKET)
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (!data) return;

        // Desestructuramos la acción y el resto del payload
        const { action, ...payload } = data;

        switch (action) {
            case 'show_lyric':
                animateLyric(payload); // Iniciamos secuencia de animación
                break;
            case 'set_colors':
                setColors({ base: payload.baseColor, fill: payload.fillColor, stroke: payload.strokeColor });
                break;
            case 'set_visible':
                setIsVisible(payload.value);
                break;
            case 'set_fill':
                setIsFillingEnabled(payload.value);
                break;
            default:
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // -------------------------------------------------------------------------
    // 3. LÓGICA DE ANIMACIÓN (Core)
    // -------------------------------------------------------------------------
    const animateLyric = (lyricData) => {
        // A) Reseteo Inmediato:
        // Quitamos la transición para que el ancho regrese a 0% instantáneamente sin animar el retroceso
        if (transitionRef.current) {
            transitionRef.current.style.transition = 'none';
            transitionRef.current.style.width = '0%';
        }
        
        // Actualizamos el texto
        setCurrentLine(lyricData);

        // Si es instrumental, terminamos aquí (se muestra el texto/ícono pero no se rellena)
        if (lyricData.mode === 'instrumental') return;

        // B) Inicio de Animación:
        if (isFillingEnabled) {
            // Usamos un pequeño Timeout (50ms) para dar tiempo al navegador de renderizar el width: 0%
            setTimeout(() => {
                if (transitionRef.current) {
                    // 1. Definimos la duración exacta que viene del backend (lineal para karaoke)
                    transitionRef.current.style.transition = `width ${lyricData.duration}ms linear`;
                    // 2. Disparamos el cambio de ancho al 100%
                    transitionRef.current.style.width = '100%';
                }
            }, 50);
        }
    };

    // -------------------------------------------------------------------------
    // 4. RENDERIZADO
    // -------------------------------------------------------------------------
    
    // Si el admin oculta la letra o no hay línea, no renderizamos nada (transparencia total)
    if (!isVisible || !currentLine) return <div className='Karaoke__Screen' />;

    return (
        <div className='Karaoke__Screen'>
            {/* Contenedor de la barra superior */}
            <div className='Karaoke__Screen__BarUp' style={{ flexDirection: 'column' }}>
                
                {/* Contenedor relativo para apilar las capas de texto */}
                <div className='Karaoke__Screen__Line'>
                    
                    {/* CAPA 1: TEXTO BASE (Color inactivo + Borde) */}
                    <div className='Karaoke__Screen__Text Karaoke__Screen__Text--Base' style={{ color: colors.base, WebkitTextStroke: `2px ${colors.stroke}` }}>{currentLine.text}</div>

                    {/* CAPA 2: TEXTO RELLENO (Color activo + Borde + Animación) */}
                    {/* Solo se renderiza si no es instrumental */}
                    {currentLine.mode !== 'instrumental' && (
                        <div className='Karaoke__Screen__Text Karaoke__Screen__Text--Fill' ref={transitionRef} style={{ color: colors.fill, WebkitTextStroke: `2px ${colors.stroke}` }}>{currentLine.text}</div>
                    )}
                </div>

                {currentLine.mode !== 'instrumental' && (
                    <p className='Karaoke__Screen__Credits'>Powered by Interaktia</p>
                )}
            </div>
        </div>
    );
};

export { Karaoke };